/**
 * Plan — a week of meals laid out before they happen.
 *
 * The one question the app could never answer. Everything else it does is
 * retrospective — what did I eat, how did the week go, what does the trend say
 * — and deciding *before* the fact is the part that actually changes an
 * outcome. There was nowhere to do it.
 *
 * A planned day is an intention, not a record, and the whole feature turns on
 * keeping those apart. Accepting a day is a separate, deliberate act: the plan
 * says what you meant to eat and the diary says what you did, and collapsing
 * them would quietly fill a diary with meals nobody ate. So a day is offered as
 * one Accept, the entries land with their planned portions and stay editable,
 * and you only touch what actually changed.
 *
 * Accepting is several writes at once, which is why the offline queue had to
 * come first. Each planned entry's log carries an idempotency key derived from
 * the entry's own id, so a retry after a dropped connection cannot log a meal
 * twice — and a partial failure names the entry that did not land instead of
 * leaving a basket half-committed with nothing on screen to say which half.
 */

import {
  localDate,
  shiftDate,
  useAcceptDay,
  useDay,
  useDeletePlannedEntry,
  useFillFromLastWeek,
  usePlan,
  useRecipes,
  useSavePlannedEntry,
  type AcceptedDay,
  type Meal,
  type PlannedEntry,
} from "@calorio/core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useCalorio } from "../app/providers";

const MEALS: Meal[] = ["breakfast", "lunch", "dinner", "snack"];
const MEAL_SHORT: Record<Meal, string> = {
  breakfast: "Breakf.",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snacks",
};

/** Monday of the week containing `date`. Weeks start on Monday here. */
function weekStart(date: string): string {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  // getUTCDay is 0 for Sunday; shift so Monday is 0.
  return shiftDate(date, -((day + 6) % 7));
}

const dayLabel = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "short", day: "numeric" });

const monthSpan = (from: string, to: string) => {
  const a = new Date(`${from}T12:00:00Z`);
  const b = new Date(`${to}T12:00:00Z`);
  const month = (d: Date) => d.toLocaleDateString(undefined, { month: "long" });
  return month(a) === month(b)
    ? `${a.getUTCDate()}–${b.getUTCDate()} ${month(b)}`
    : `${a.getUTCDate()} ${month(a)} – ${b.getUTCDate()} ${month(b)}`;
};

export function Plan() {
  const ctx = useCalorio();
  const today = localDate(ctx.timeZone);
  const [anchor, setAnchor] = useState(today);

  const from = weekStart(anchor);
  const to = shiftDate(from, 6);
  const days = Array.from({ length: 7 }, (_, i) => shiftDate(from, i));

  /**
   * Fourteen days fetched for a seven-day grid, because "Fill from last week"
   * cannot be offered honestly without knowing whether last week has anything
   * in it. The alternative was a second query for a range nobody draws; one
   * widened range asks the same server once and the grid keeps filtering by
   * date, so the extra week is read and never rendered.
   */
  const plan = usePlan(ctx, shiftDate(from, -7), to);
  const fill = useFillFromLastWeek(ctx);
  const accept = useAcceptDay(ctx);
  const day = useDay(ctx, today);
  const recipes = useRecipes(ctx);
  const savePlanned = useSavePlannedEntry(ctx);
  const removePlanned = useDeletePlannedEntry(ctx);

  /** The cell currently being filled, as `date|meal`. */
  const [filling, setFilling] = useState<string | null>(null);

  /** The last accept's per-entry outcome, kept so a partial failure is nameable. */
  const [outcome, setOutcome] = useState<AcceptedDay | null>(null);

  const entries = plan.data ?? [];
  const forDay = (date: string) => entries.filter((e) => e.local_date === date);
  const cell = (date: string, meal: Meal) =>
    entries.filter((e) => e.local_date === date && e.meal === meal);

  const plannedDays = days.filter((d) => forDay(d).length > 0).length;

  /**
   * Whether there is a week behind this one to copy from.
   *
   * The query starts seven days before the grid does, so anything dated before
   * the Monday on screen is last week by construction — no second request and
   * no date arithmetic beyond the comparison. Undefined data is not "no": the
   * button waits for the answer rather than appearing a moment later.
   */
  const hasLastWeek = entries.some((e) => e.local_date < from);

  const todayEntries = forDay(today);
  const unaccepted = todayEntries.filter((e) => !e.accepted_at);

  const acceptToday = async () => {
    setOutcome(null);
    try {
      setOutcome(await accept.mutateAsync(today));
    } catch {
      // The mutation's own error state carries this; the card reads it below.
    }
  };

  return (
    <div className="stack">
      <header className="plan__head">
        <div>
          <h1 className="plan__title">Plan · {monthSpan(from, to)}</h1>
          <p className="stat__label" style={{ margin: 0 }}>
            Planned <span className="num">{plannedDays}</span> of <span className="num">7</span>{" "}
            days
          </p>
        </div>

        <div className="plan__actions">
          <button className="plan__pill" onClick={() => setAnchor(shiftDate(from, -7))}>
            ‹ Previous
          </button>
          <button className="plan__pill" onClick={() => setAnchor(shiftDate(from, 7))}>
            Next ›
          </button>
          {/* Only once there is a week behind you. Offering it on day one is an
              offer to copy nothing, which reads as a broken button. */}
          {plan.data && hasLastWeek && (
            <button
              className="plan__pill plan__pill--action"
              onClick={() => fill.mutate({ from_date: from, to_date: to })}
              disabled={fill.isPending}
            >
              {fill.isPending ? "Filling…" : "Fill from last week"}
            </button>
          )}
        </div>
      </header>

      {plan.isLoading && <p className="muted">Loading the week…</p>}

      {/* The grid below is twenty-eight cells of "+ plan", every one of which
          leads to the same dead end on a new account: planning is done from
          saved meals, and there are none. Said once, up here, where it is read
          before the tapping starts rather than after. Both answers have to be
          in before it can be said at all — a week with a plan in it, or a
          cupboard with a meal in it, each make this the wrong thing to say. */}
      {plan.data && recipes.data && plannedDays === 0 && recipes.data.length === 0 && (
        <div className="card stack" style={{ gap: "var(--sp-2)" }}>
          <p className="manual__note">
            Nothing planned, and nothing to plan from. A day here is built out of saved meals —
            compose one and it becomes something you can drop into any cell, accept on the day it
            comes round, and shop for a week at a time.
          </p>
          <p className="manual__note">
            <Link to="/recipes/new" className="muted">
              Compose a saved meal →
            </Link>
          </p>
        </div>
      )}

      {plan.data && (
        <div className="plan__body">
          <div className="plan__grid-wrap">
            <div className="plan__grid">
              {/* The corner above the meal names. Empty, but it has to be a
                  real cell: it is the top of the pinned column, and without it
                  the day headings would scroll under nothing. */}
              <div className="plan__corner" />
              {days.map((date) => (
                <div className="plan__col-head" key={date}>
                  <span className="plan__col-day">{dayLabel(date)}</span>
                  <span className={`plan__col-state${date === today ? " is-today" : ""}`}>
                    {date === today
                      ? "today"
                      : forDay(date).length === 0
                        ? "empty"
                        : date < today
                          ? "past"
                          : "planned"}
                  </span>
                </div>
              ))}

              {MEALS.map((meal) => (
                <div className="plan__row" key={meal} style={{ display: "contents" }}>
                  <div className="plan__row-head">{MEAL_SHORT[meal]}</div>
                  {days.map((date) => {
                    const here = cell(date, meal);
                    return (
                      <div className="plan__cell" key={`${date}-${meal}`}>
                        {here.map((entry) => (
                          <PlanCell
                            key={entry.id}
                            entry={entry}
                            onRemove={() => removePlanned.mutate(entry.id)}
                          />
                        ))}

                        {/* Planning from saved meals only, deliberately. A plan
                            made of single ingredients is a shopping list you
                            have to cook from twice; the recipes are the unit
                            the rest of the feature — portions, leftovers, the
                            shopping subtraction — is built on. */}
                        {filling === `${date}|${meal}` ? (
                          <div className="plan__pick">
                            {recipes.data?.length ? (
                              recipes.data.map((recipe) => (
                                <button
                                  key={recipe.id}
                                  className="plan__pick-row"
                                  onClick={() => {
                                    savePlanned.mutate({
                                      local_date: date,
                                      meal,
                                      recipe_id: recipe.id,
                                      portions: 1,
                                    });
                                    setFilling(null);
                                  }}
                                >
                                  {recipe.name}
                                </button>
                              ))
                            ) : (
                              <Link to="/recipes/new" className="plan__pick-row">
                                Compose a saved meal first →
                              </Link>
                            )}
                            <button className="plan__pick-row" onClick={() => setFilling(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          !here.length && (
                            <button
                              className="plan__empty"
                              onClick={() => setFilling(`${date}|${meal}`)}
                            >
                              + plan
                            </button>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <aside className="plan__rail stack">
            {/* Today's plan, and the one button that turns it into a diary. */}
            {todayEntries.length > 0 && (
              <div className="plan__accept">
                <span className="plan__accept-label">
                  {unaccepted.length ? "Today, not yet accepted" : "Today, accepted"}
                </span>

                {todayEntries.map((entry) => (
                  <div className="plan__accept-row" key={entry.id}>
                    <span>{MEAL_SHORT[entry.meal]}</span>
                    <span className={entry.accepted_at ? "plan__accepted" : "num"}>
                      {entry.accepted_at ? "accepted" : `${Math.round(entry.quantity_g)} g`}
                    </span>
                  </div>
                ))}

                {unaccepted.length > 0 && (
                  <button
                    className="plan__accept-go"
                    onClick={() => void acceptToday()}
                    disabled={accept.isPending}
                  >
                    {accept.isPending ? "Accepting…" : "Accept the rest of today"}
                  </button>
                )}

                <p className="manual__note">
                  Entries are logged at the time you accept, with their planned portions, and stay
                  editable. Anything already logged is left alone. Swapping a meal afterwards
                  changes the day, not the plan.
                </p>
              </div>
            )}

            {/* The partial case, which is the whole reason this reports per
                entry: two landed and one did not, and the user needs to know
                which — not that "something went wrong". */}
            {outcome && outcome.logged < outcome.requested && (
              <div className="plan__partial">
                <div className="plan__partial-head">
                  <span>Accepted today&rsquo;s plan</span>
                  <span className="num stat__label">
                    {outcome.logged} of {outcome.requested} logged
                  </span>
                </div>
                {outcome.results
                  .filter((r) => !r.logged)
                  .map((r) => (
                    <p className="plan__partial-row" key={r.planned_entry_id}>
                      {r.error ?? "Didn't send — the connection dropped."} Still here, nothing was
                      logged twice.
                    </p>
                  ))}
                <button
                  className="plan__pill plan__pill--action"
                  onClick={() => void acceptToday()}
                  disabled={accept.isPending}
                >
                  Retry everything unsent
                </button>
                <p className="manual__note">
                  Each write carries its own id, so a retry can&rsquo;t duplicate.
                </p>
              </div>
            )}

            {accept.isError && !outcome && (
              <p className="error" style={{ margin: 0 }}>
                {String(accept.error)}
              </p>
            )}

            <div className="plan__week">
              <span className="plan__accept-label">Week, as planned</span>
              <div className="plan__accept-row">
                <span>Days planned</span>
                <span className="num">
                  {plannedDays} / 7
                </span>
              </div>
              <div className="plan__accept-row">
                <span>Today logged</span>
                <span className="num">
                  {day.data ? Math.round(day.data.totals.kcal).toLocaleString() : "—"} kcal
                </span>
              </div>
            </div>

            <Link to="/plan/shopping" className="plan__pill plan__pill--action plan__shop">
              Shopping for this week
            </Link>
          </aside>
        </div>
      )}
    </div>
  );
}

/** One planned meal in the grid. */
function PlanCell({ entry, onRemove }: { entry: PlannedEntry; onRemove: () => void }) {
  return (
    <div className="plan__entry">
      <span className="plan__entry-grams num">{Math.round(entry.quantity_g)} g</span>
      <span className={`plan__entry-state${entry.accepted_at ? " is-accepted" : ""}`}>
        {entry.accepted_at ? "accepted" : "planned"}
      </span>
      {/* Only while it is still an intention. Once accepted there is a diary
          entry, and removing the plan behind it would leave the meal logged
          with nothing to explain where it came from — that is an edit to the
          day, made on Today. */}
      {!entry.accepted_at && (
        <button className="plan__entry-remove" onClick={onRemove} aria-label="Remove from plan">
          ×
        </button>
      )}
    </div>
  );
}
